import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
  Box,
  Stack,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import type { AIGenerationDialogProps } from './types'
import { FIELD_SECTIONS } from './types'

export default function AIGenerationDialog({
  open,
  onClose,
  onGenerate,
  prompt,
  loading = false,
}: AIGenerationDialogProps) {
  const [additionalPrompt, setAdditionalPrompt] = useState('')

  // すべてのフィールドを取得
  const allFieldNames = useMemo(
    () => FIELD_SECTIONS.flatMap((section) => section.fields.map((field) => field.name)),
    [],
  )

  // デフォルトで全選択
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set(allFieldNames))

  // セクションの展開状態（デフォルトで全閉じ）
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set())

  // ダイアログが開かれたときに全選択状態にリセット（セクションは閉じる）
  useEffect(() => {
    if (open) {
      setSelectedFields(new Set(allFieldNames))
      setExpandedSections(new Set())
    }
  }, [open, allFieldNames])

  // セクションの展開/折りたたみ
  const toggleSection = useCallback(
    (index: number) => {
      const newExpanded = new Set(expandedSections)
      if (newExpanded.has(index)) {
        newExpanded.delete(index)
      } else {
        newExpanded.add(index)
      }
      setExpandedSections(newExpanded)
    },
    [expandedSections],
  )

  const handleFieldToggle = useCallback(
    (fieldName: string) => {
      const newSelected = new Set(selectedFields)
      if (newSelected.has(fieldName)) {
        newSelected.delete(fieldName)
      } else {
        newSelected.add(fieldName)
      }
      setSelectedFields(newSelected)
    },
    [selectedFields],
  )

  // セクション全体のトグル
  const handleSectionToggle = useCallback(
    (sectionIndex: number) => {
      const section = FIELD_SECTIONS[sectionIndex]
      const sectionFieldNames = section.fields.map((f) => f.name)
      const allSelected = sectionFieldNames.every((name) => selectedFields.has(name))

      const newSelected = new Set(selectedFields)
      if (allSelected) {
        // 全選択なら全解除
        sectionFieldNames.forEach((name) => newSelected.delete(name))
      } else {
        // 一部または未選択なら全選択
        sectionFieldNames.forEach((name) => newSelected.add(name))
      }
      setSelectedFields(newSelected)
    },
    [selectedFields],
  )

  // セクションの選択状態を取得
  const getSectionCheckState = useCallback(
    (
      sectionIndex: number,
    ): {
      checked: boolean
      indeterminate: boolean
    } => {
      const section = FIELD_SECTIONS[sectionIndex]
      const sectionFieldNames = section.fields.map((f) => f.name)
      const selectedCount = sectionFieldNames.filter((name) => selectedFields.has(name)).length

      if (selectedCount === 0) {
        return { checked: false, indeterminate: false }
      } else if (selectedCount === sectionFieldNames.length) {
        return { checked: true, indeterminate: false }
      } else {
        return { checked: false, indeterminate: true }
      }
    },
    [selectedFields],
  )

  // 全選択
  const handleSelectAll = useCallback(() => {
    setSelectedFields(new Set(allFieldNames))
  }, [allFieldNames])

  // 全解除
  const handleDeselectAll = useCallback(() => {
    setSelectedFields(new Set())
  }, [])

  const handleGenerate = useCallback(() => {
    if (selectedFields.size === 0) {
      return
    }
    onGenerate(additionalPrompt, Array.from(selectedFields))
  }, [onGenerate, additionalPrompt, selectedFields])

  const handleClose = useCallback(() => {
    if (!loading) {
      onClose()
    }
  }, [loading, onClose])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { minHeight: '600px' },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoFixHighIcon />
        AI生成
      </DialogTitle>
      <DialogContent>
        {/* プロンプト説明 */}
        {prompt.Description__c && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {prompt.Description__c}
            </Typography>
          </Box>
        )}
        対象フィールド選択
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            対象項目
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<CheckBoxIcon />}
              onClick={handleSelectAll}
              disabled={loading}
              variant="outlined"
            >
              全選択
            </Button>
            <Button
              size="small"
              startIcon={<CheckBoxOutlineBlankIcon />}
              onClick={handleDeselectAll}
              disabled={loading}
              variant="outlined"
            >
              全解除
            </Button>
          </Stack>
        </Box>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          {FIELD_SECTIONS.map((section, index) => {
            const checkState = getSectionCheckState(index)
            const expanded = expandedSections.has(index)

            return (
              <Box key={section.title}>
                {/* セクションヘッダー */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderBottom: expanded ? 1 : 0,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                  onClick={() => toggleSection(index)}
                >
                  <Box
                    sx={{
                      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      mr: 1,
                    }}
                  >
                    <ChevronRightIcon />
                  </Box>
                  <Checkbox
                    checked={checkState.checked}
                    indeterminate={checkState.indeterminate}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleSectionToggle(index)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={loading}
                    sx={{ p: 0, mr: 1 }}
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                    {section.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {section.fields.filter((f) => selectedFields.has(f.name)).length} / {section.fields.length}
                  </Typography>
                </Box>

                {/* セクション内の項目（ツリー構造） */}
                {expanded && (
                  <Box sx={{ bgcolor: 'background.paper' }}>
                    <FormGroup sx={{ pl: 6, pr: 2, py: 1 }}>
                      {section.fields.map((field) => (
                        <FormControlLabel
                          key={field.name}
                          control={
                            <Checkbox
                              checked={selectedFields.has(field.name)}
                              onChange={() => handleFieldToggle(field.name)}
                              disabled={loading}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">{field.label}</Typography>}
                          sx={{ ml: 0, my: 0.25 }}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
        {/* 追加プロンプト */}
        <TextField
          label="追加プロンプト"
          multiline
          rows={4}
          fullWidth
          value={additionalPrompt}
          onChange={(e) => setAdditionalPrompt(e.target.value)}
          disabled={loading}
          sx={{ mt: 3 }}
          placeholder="追加の指示があれば入力してください"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          キャンセル
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={loading || selectedFields.size === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
        >
          {loading ? '生成中...' : '生成'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
